import { useId, useLayoutEffect, useRef, useState, type ComponentType } from "react";
import { cn } from "@/lib/utils";

export type SegmentOption<V extends string> = {
  value: V;
  label: string;
  icon?: ComponentType<{ className?: string }>;
};

interface SegmentedToggleProps<V extends string> {
  options: SegmentOption<V>[];
  value: V;
  onChange: (v: V) => void;
  ariaLabel?: string;
  className?: string;
}

/**
 * Premium segmented toggle with an animated sliding indicator.
 * Designed to scale to N segments (Picks, Parlays, Props, Live, …).
 */
export default function SegmentedToggle<V extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: SegmentedToggleProps<V>) {
  const groupId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const btn = btnRefs.current[value];
    if (!container || !btn) return;
    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    setIndicator({ left: bRect.left - cRect.left, width: bRect.width });
  }, [value, options.length]);

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "relative inline-flex items-center p-1 rounded-xl border border-border/60 bg-secondary/60 backdrop-blur-sm shadow-inner",
        className,
      )}
    >
      {indicator && (
        <span
          aria-hidden
          className="absolute top-1 bottom-1 rounded-lg bg-gradient-to-br from-primary/25 via-primary/15 to-accent/20 ring-1 ring-primary/40 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)] transition-all duration-300 ease-out"
          style={{
            transform: `translateX(${indicator.left - 4}px)`,
            width: indicator.width,
          }}
        />
      )}
      {options.map((opt) => {
        const Icon = opt.icon;
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => (btnRefs.current[opt.value] = el)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${groupId}-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative z-10 inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200",
              active
                ? "text-foreground drop-shadow-[0_0_6px_hsl(var(--primary)/0.45)]"
                : "text-muted-foreground hover:text-foreground hover:bg-primary/5",
            )}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
